<template>
  <div v-if="isShowComponent">
    <AppointmentCalendarSkeleton
      v-show="isLoading"
      class="px-2"
      :is-collapsed="isCollapsed"
    />
    <AppointmentDataCard
      v-show="!isLoading"
      ref="appointment-calendar"
      data-qa="appointment_calendar"
      qa-locator="appointment_data_card"
      prepend-icon="ui-icon-calendar-date-and-clock"
      class="px-2 overflow-visible"
      card-class="py-2"
      min-height="56"
      :name="calendarTitle"
      :subtitle="durationServiceText"
      :is-error-state="isErrorState"
      :has-primary-border="hasPrimaryBorder"
      :error-description="errorDescription"
    >
      <template
        v-if="isCollapsed && !editableCalendar.selectedDate.isSlotFixed"
        #icon
      >
        <div
          class="ui-icon-pen"
          data-qa="appointment_calendar_click_listener"
          :style="{ cursor: 'pointer' }"
          @click="handleClickOpenSchedule"
        />
      </template>
      <BaseAlert
        v-if="isShowNearestAvailableBanner"
        class="mb-4"
        color="text-error"
        data-qa="appointment_calendar_warning"
      >
        {{ $t('Клиника не работает в это время.', options) }}<br>
        {{ $t('Ближайшее время для записи:', options) }} {{ nearestAvailableDate }}
      </BaseAlert>
      <v-card
        v-if="isShowCardAppointment"
        max-width="initial"
        color=""
        :elevation="0"
      >
        <template
          v-if="!isEmptyCalendarList"
        >
          <div
            class="pb-2"
            :class="{
              'mx-n2': isMobile && (hasPrimaryBorder || isErrorState) || !isMobile,
              'mx-n4': isMobile && !(hasPrimaryBorder || isErrorState),
            }"
          >
            <AppointmentCalendar
              v-if="!isLoading"
              data-qa="appointment_calendar_component"
              :padding-x="isMobile ? (hasPrimaryBorder || isErrorState ? 8 : 16) : 8"
              :slots="editableCalendar.calendarList"
              :init-day="initSlot.day"
              :fog="!isMobile && !(hasPrimaryBorder || isErrorState)"
              @calendar:time-choice="handleCalendarTimeChoice"
            />
          </div>
        </template>
        <v-btn
          v-if="isShowDoctorRedirectButton"
          :block="isMobile"
          :width="isMobile ? '' : '389px'"
          :elevation="0"
          :class="{
            'mb-4': isMobile,
          }"
          color="primary"
          @click="handleClickRedirectDoctor"
        >
          {{ $t('На страницу врача', options) }}
        </v-btn>
      </v-card>
    </AppointmentDataCard>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { declensionByNum, setQueryParam } from 'www/utils'
import { mapState, mapActions } from 'pinia'

import { TOWN_ONLINE_GOALS } from 'www/yandexGoals'
import BaseAlert from 'www/vue_components/common/core/BaseAlert.vue'

import AppointmentCalendar from 'www/vue_components/common/AppointmentCalendar/AppointmentCalendar.vue'
import AppointmentCalendarSkeleton from 'www/vue_components/common/AppointmentPage/components/common/AppointmentCalendarSkeleton.vue'
import AppointmentPageMethods from 'www/vue_components/common/AppointmentPage/mixins/AppointmentPageMethods'
import { APPOINTMENT_ERRORS, RECURSIVE_INTERVAL_SCHEDULE_CHECKS_DOCTOR_MS } from 'www/vue_components/common/AppointmentPage/constants'
import AppointmentDataCard from 'www/vue_components/common/AppointmentDataCard/AppointmentDataCard.vue'
import {
  useUserDataStore,
  useGlobalDataStore,
  useDoctorDataStore,
  useServiceDataStore,
  useYandexGoalsStore,
  useCalendarDataStore,
  useAuthorizationStore, // todo(useAuthMt) - удалить
  useAppointmentDataStore,
  usePreliminaryConsultationStore,
} from 'www/vue_components/common/AppointmentPage/stores'

import { useDoctorAutomaticScheduleChecks } from 'www/vue_components/common/AppointmentPage/subStores'
import useNearestSlotsStore from 'www/vue_components/common/AppointmentPage/stores/useNearestSlotsStore'
import getUserLanguage from 'src/utils/getUserLanguage'

export default {
  name: 'AppointmentEditableCalendar',
  components: {
    AppointmentDataCard,
    BaseAlert,
    AppointmentCalendar,
    AppointmentCalendarSkeleton,
  },
  mixins: [
    AppointmentPageMethods,
  ],
  data: vm => ({
    vuetifyColors: vm.$vuetify.theme.themes.light,
    isMobile: window.MOBILE_VERSION,
    isChangingState: false,
    options: { ns: getUserLanguage() },
  }),
  computed: {
    ...mapState(useDoctorDataStore, [
      'doctorData',
      'selectedWorkplace',
      'scheduleType',
      'isWorkplaceSelected',
      'hasDifferentTelemedPrices',
    ]),
    ...mapState(useCalendarDataStore, [
      'initCalendarData',
      'editableCalendar',
      'isLoadingSlots',
      'isErrorState',
    ]),
    ...mapState(useUserDataStore, [
      'userTimeDelta',
    ]),
    ...mapState(useAuthorizationStore, [ // todo(useAuthMt) - удалить
      'isPhoneConfirmed', // todo(useAuthMt) - удалить
    ]),
    ...mapState(useAppointmentDataStore, [
      'appointmentRegistrationTypes',
      'appointmentObjectTypes',
      'appointmentPlaceTypes',
      'initAdditionalData',
      'useAuthMt',
    ]),
    ...mapState(useCalendarDataStore, [
      'isEmptyCalendarList',
      'editableCalendarTitle',
      'nearestFreeAppointmentDate',
      'isAvailableSlot',
      'isCalendarDateSelected',
      'isValidCalendar',
      'selectedFullDate',
      'hasSlotsRequestData',
    ]),
    ...mapState(useServiceDataStore, [
      'isLoadingRelations',
      'doctorEquipmentRelations',
    ]),
    ...mapState(useGlobalDataStore, [
      'errorData',
    ]),
    ...mapState(usePreliminaryConsultationStore, [
      'isPreliminaryConsultation',
    ]),
    ...mapState(usePreliminaryConsultationStore, [
      'isSelectedDoctorConsultation',
      'useServiceExpressWithoutConsultation',
    ]),
    ...mapState(useDoctorAutomaticScheduleChecks, [
      'currentRequestAttempt',
    ]),
    nearestAvailableDate() {
      if (!this.nearestFreeAppointmentDate) {
        return ''
      }

      return format(this.nearestFreeAppointmentDate, 'd MMMM, H:mm', { locale: ru })
    },
    initSlot() {
      if (this.editableCalendar.selectedDate.isSlotFixed) {
        return {}
      }

      return {
        day: this.editableCalendar.selectedDate.day,
        time: this.editableCalendar.selectedDate.time,
      }
    },
    isCollapsed() {
      return Boolean(this.editableCalendarTitle && !this.isChangingState)
    },
    useRelationsRequest() {
      return this.appointmentObjectTypes.isServices
        && !this.isPreliminaryConsultation
        && this.hasSlotsRequestData
    },
    usePreliminaryConsultationRequest() {
      return this.appointmentObjectTypes.isServices
        && this.isPreliminaryConsultation
        && !this.useServiceExpressWithoutConsultation
    },
    calendarTitle() {
      if (
        this.appointmentObjectTypes.isDoctor
        && this.appointmentRegistrationTypes.isDirect
        && this.isEmptyCalendarList
        && !this.editableCalendar.selectedDate.isSlotFixed
      ) {
        return this.$t('У клиники временно не работает онлайн запись', this.options)
      }

      return this.isCalendarDateSelected
        ? this.editableCalendarTitle
        : this.$t('Выберите дату и время', this.options)
    },
    errorDescription() {
      if (this.appointmentObjectTypes.isDoctor && !this.isEmptyCalendarList) {
        const { day, time } = this.editableCalendar.selectedDate

        if (day && time) {
          const hasTime = this.testHasTime({
            selectedDate: { day, time },
          })

          if (!hasTime && this.isErrorState) {
            return this.$t('Это время кто-то только что занял. Выберите другое.', this.options)
          }
        }
      }

      return ''
    },
    hasPrimaryBorder() {
      return this.appointmentObjectTypes.isDoctor
        && !this.isEmptyCalendarList
        && !this.isErrorState
        && !this.editableCalendar.selectedDate.day
        && !this.editableCalendar.selectedDate.time
    },
    isShowComponent() {
      if (this.appointmentRegistrationTypes.isExpress) {
        return false
      }

      if (this.appointmentObjectTypes.isDoctor && !this.appointmentRegistrationTypes.isExpress) {
        return true
      }

      return this.isLoading || !this.isEmptyCalendarList || this.editableCalendar.selectedDate.isSlotFixed
    },
    isShowCardAppointment() {
      if (this.appointmentObjectTypes.isDoctor) {
        if (this.appointmentRegistrationTypes.isDirect || !this.isEmptyCalendarList) {
          return !this.isCollapsed
        }

        return false
      }

      return !this.isCollapsed && !this.isEmptyCalendarList
    },
    isShowDoctorRedirectButton() {
      return this.appointmentObjectTypes.isDoctor
        && this.isEmptyCalendarList
        && this.appointmentRegistrationTypes.isDirect
    },
    isShowNearestAvailableBanner() {
      return !this.isValidCalendar
        && !this.isAvailableSlot
        && (this.isPhoneConfirmed || this.useAuthMt)
        && this.isCalendarDateSelected
        && this.nearestAvailableDate
    },
    durationServiceText() {
      if (this.appointmentObjectTypes.isServices) {
        const { duration } = this.editableCalendar.selectedDate

        if (!duration) {
          return ''
        }

        const hours = Math.floor(duration / 60) // 60 минут. Duration на услугах приходит в минутах. На врачах в секундах
        const minutes = duration % 60 // 60 минут. Duration на услугах приходит в минутах. На врачах в секундах
        const hoursText = hours ? `${hours} ${declensionByNum(hours, ['час', 'часа', 'часов'])}` : ''
        const minutesText = minutes ? `${minutes} ${declensionByNum(minutes, ['минута', 'минуты', 'минут'])}` : ''

        return duration ? `Длительность: ${hoursText} ${minutesText}` : ''
      }

      return ''
    },
  },
  // Порядок важен: что раньше объявлено, то раньше сработает
  watch: {
    'editableCalendar.isChanging': {
      immediate: true,
      handler(isChanging) {
        this.callOrOnceWatch(
          () => !this.isLoading,
          () => {
            this.isChangingState = isChanging
          },
        )
      },
    },
    isLoadingSlots: {
      immediate: true,
      handler(isLoading) {
        this.timeoutLoading(isLoading)
      },
    },
  },
  mounted() {
    this.updateAppointmentCalendarEl(this.$refs['appointment-calendar']?.$el)
    this.initData()
    this.isLoading = true
    this.updateIsLoadingSlots(true)

    if (this.appointmentObjectTypes.isDoctor) {
      this.callOrOnceWatch(
        () => (this.isPhoneConfirmed || this.useAuthMt)
          && !this.errorData.isEnabled
          && (
            (!this.appointmentPlaceTypes.isTelemed && !this.appointmentPlaceTypes.isTelemedMedtochka)
            || this.isWorkplaceSelected
          ),
        this.initRequestForSlots,
      )
    }
  },
  methods: {
    handleClickRedirectDoctor() {
      const path = this.doctorData.pageUrl ? `${window.location.origin}${this.doctorData.pageUrl}` : document.referrer

      window.location.assign(path)
    },
    initData() {
      const {
        day,
        time,
        duration,
        dateName,
        isSlotFixed,
      } = this.initCalendarData
      const selectedDate = this.isCalendarDateSelected
        ? this.editableCalendar.selectedDate
        : {
          day,
          time,
          duration,
          dateName,
          isSlotFixed,
        }

      this.updateSelectedDate(selectedDate)
      this.updateLastSelectedDate(this.editableCalendar.selectedDate)
    },
    async initRequestForSlots() {
      if (!this.editableCalendar.selectedDate.isSlotFixed) {
        if (this.appointmentPlaceTypes.isTelemed || this.appointmentPlaceTypes.isTelemedMedtochka) {
          if (!await this.changePlaceTypeIfNeeded()) {
            this.requestForSlots({
              errorRequestCallback: this.showErrorState,
            })
          }

          return
        }

        this.requestForSlots({
          afterRequestCallback: () => {
            if (this.isPreliminaryConsultation && this.isEmptyCalendarList) {
              this.updateIsLoadingDoctorsAndConsultations(false)
            }
          },
          errorRequestCallback: this.showErrorState,
        })
      }
    },
    async handleCalendarTimeChoice(data) {
      this.updateIsErrorState(false)

      if (this.currentRequestAttempt === 0) {
        setTimeout(() => {
          this.startRecursiveUpdateDoctorSchedule()
        }, RECURSIVE_INTERVAL_SCHEDULE_CHECKS_DOCTOR_MS)
      }

      if (this.selectedFullDate !== data.selectedFullDate) {
        this.sendYaGoal({
          name: TOWN_ONLINE_GOALS.chooseAnotherTime,
          formType: 'telemedAndTownOnline',
          objectType: 'doctor',
        })
      }

      this.updateSelectedDate({
        ...data,
        isSlotFixed: false,
      })
      this.updateLastSelectedDate(this.editableCalendar.selectedDate)

      if (
        (this.appointmentPlaceTypes.isTelemed || this.appointmentPlaceTypes.isTelemedMedtochka)
        && this.scheduleType === 'all'
      ) {
        await this.changePlaceTypeIfNeeded()
      }

      this.updateCalendarIsChanging(false)
      this.updateIsErrorState(false)

      if (this.useRelationsRequest) {
        this.updateIsLoadingSlots(true)
        await this.requestDoctorEquipmentRelations(data)
        this.updateIsLoadingSlots(false)
      }

      if (this.isCalendarDateSelected) {
        setQueryParam('slot', data.selectedFullDate)
      }

      if (this.usePreliminaryConsultationRequest) {
        this.updateIsLoadingSlots(true)
        await this.requestDoctorsAndConsultations(data)

        if (this.isSelectedDoctorConsultation || this.useServiceExpressWithoutConsultation) {
          this.updateIsLoadingSlots(false)
        }
      }
    },
    handleClickOpenSchedule() {
      this.sendYaGoal({
        name: TOWN_ONLINE_GOALS.openScheduleDoctor,
        formType: 'telemedAndTownOnline',
        objectType: 'doctor',
      })

      this.updateCalendarIsChanging(true)
    },
    showErrorState() {
      this.isLoading = false

      if (this.useServiceExpressWithoutConsultation) {
        this.updateIsLoadingDoctorsAndConsultations(false)
        return
      }

      if (!this.appointmentRegistrationTypes.isDirect) {
        return
      }

      this.updateErrorData({
        isDialogRender: true,
        isEnabled: true,
        ...APPOINTMENT_ERRORS.appointmentDateNotAvailable,
      })
    },
    ...mapActions(useAppointmentDataStore, [
      'updateInitAdditionalPlaceType',
    ]),
    ...mapActions(useCalendarDataStore, [
      'updateSelectedDate',
      'updateLastSelectedDate',
      'updateAppointmentCalendarEl',
      'updateCalendarIsChanging',
      'updateIsLoadingSlots',
      'updateIsErrorState',
    ]),
    ...mapActions(useNearestSlotsStore, [
      'changePlaceTypeIfNeeded',
    ]),
    ...mapActions(useGlobalDataStore, [
      'updateErrorData',
    ]),
    ...mapActions(useCalendarDataStore, [
      'requestForSlots',
      'startRecursiveUpdateDoctorSchedule',
    ]),
    ...mapActions(usePreliminaryConsultationStore, [
      'updateIsLoadingDoctorsAndConsultations',
    ]),
    ...mapActions(usePreliminaryConsultationStore, [
      'requestDoctorsAndConsultations',
    ]),
    ...mapActions(useServiceDataStore, [
      'updateIsLoadingRelations',
    ]),
    ...mapActions(useServiceDataStore, [
      'requestDoctorEquipmentRelations',
    ]),
    ...mapActions(useYandexGoalsStore, [
      'sendYaGoal',
    ]),
    ...mapActions(useDoctorAutomaticScheduleChecks, [
      'testHasTime',
    ]),
  },
}
</script>
